"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generatePresentationAwareProductFilter = exports.createProductPresentationAttributeStateFilter = void 0;
const graphql_1 = require("../../../../__generated__/graphql");
const ui_components_1 = require("@groupby/ui-components");
const table_filter_shared_utils_1 = require("./table-filter-shared.utils");
const utils_1 = require("./utils");
const attributes_1 = require("../../../constants/attributes");
/**
 * Converts AI-enriched multiselect state to the backend presentation filter contract.
 * `values.in` keeps selected known values, `values.notIn` excludes known values,
 * and `includeUnknown` requests true Unknown rows for the same label.
 */
const createProductPresentationAttributeStateFilter = (attributeName, filter, operator) => {
    var _a, _b, _c, _d, _e;
    if (filter.selectAll) {
        const excluded = (_a = filter.excludedValues) !== null && _a !== void 0 ? _a : [];
        if (operator === ui_components_1.ListFilterOperatorTypes.IsNot) {
            const hasUnknown = excluded.includes(attributes_1.SpecialAttributeValue.Unknown);
            const realValues = excluded.filter((value) => value !== attributes_1.SpecialAttributeValue.Unknown);
            if (!hasUnknown && !realValues.length) {
                return null;
            }
            return (0, utils_1.createProductPresentationAttributeFilter)(attributeName, realValues, hasUnknown);
        }
        if (!excluded.length) {
            return null;
        }
        const isUnknownExcluded = excluded.includes(attributes_1.SpecialAttributeValue.Unknown);
        const excludedWithoutUnknown = excluded.filter((value) => value !== attributes_1.SpecialAttributeValue.Unknown);
        return (0, utils_1.createProductPresentationAttributeFilter)(attributeName, [], !isUnknownExcluded, excludedWithoutUnknown);
    }
    const excluded = (_b = filter.excludedValues) !== null && _b !== void 0 ? _b : [];
    if (excluded.length) {
        const isUnknownExcluded = excluded.includes(attributes_1.SpecialAttributeValue.Unknown);
        const excludedWithoutUnknown = excluded.filter((value) => value !== attributes_1.SpecialAttributeValue.Unknown);
        return (0, utils_1.createProductPresentationAttributeFilter)(attributeName, [], !isUnknownExcluded, excludedWithoutUnknown);
    }
    const hasUnknown = (_c = filter.list) === null || _c === void 0 ? void 0 : _c.includes(attributes_1.SpecialAttributeValue.Unknown);
    const realValues = (_e = (_d = filter.list) === null || _d === void 0 ? void 0 : _d.filter((v) => v !== attributes_1.SpecialAttributeValue.Unknown)) !== null && _e !== void 0 ? _e : [];
    if (!hasUnknown && !realValues.length) {
        return null;
    }
    return (0, utils_1.createProductPresentationAttributeFilter)(attributeName, realValues, hasUnknown);
};
exports.createProductPresentationAttributeStateFilter = createProductPresentationAttributeStateFilter;
const isExclusionOnlyPresentationAttributeFilter = ({ includeUnknown, values }) => {
    var _a, _b;
    return (!((_a = values === null || values === void 0 ? void 0 : values.in) === null || _a === void 0 ? void 0 : _a.length)
        && (!includeUnknown || !!((_b = values === null || values === void 0 ? void 0 : values.notIn) === null || _b === void 0 ? void 0 : _b.length)));
};
const mergeExclusionOnlyPresentationAttributeFilters = (filters) => {
    const mergedFilters = [];
    filters.forEach((filter) => {
        var _a, _b, _c, _d;
        const existingFilterIndex = mergedFilters.findIndex((item) => item.label === filter.label);
        const existingFilter = mergedFilters[existingFilterIndex];
        if (!existingFilter
            || !isExclusionOnlyPresentationAttributeFilter(existingFilter)
            || !isExclusionOnlyPresentationAttributeFilter(filter)) {
            mergedFilters.push(filter);
            return;
        }
        const excludedValues = Array.from(new Set([
            ...((_b = (_a = existingFilter.values) === null || _a === void 0 ? void 0 : _a.notIn) !== null && _b !== void 0 ? _b : []),
            ...((_d = (_c = filter.values) === null || _c === void 0 ? void 0 : _c.notIn) !== null && _d !== void 0 ? _d : []),
        ]));
        const includeUnknown = Boolean(existingFilter.includeUnknown && filter.includeUnknown);
        mergedFilters[existingFilterIndex] = Object.assign(Object.assign({ label: existingFilter.label }, excludedValues.length && { values: { notIn: excludedValues } }), includeUnknown && { includeUnknown });
    });
    return mergedFilters;
};
const hasProductFilterCriteria = (productFilter) => (Object.keys(productFilter).some((key) => key !== 'filterStrategy'));
/**
 * Builds the shared presentation-aware filter carrier from table filter state.
 * Custom fields, measures, and original attributes stay inside `product`; AI-enriched text attributes become top-level
 * `presentationAttributes` so queries and batch actions use the same contract.
 */
const generatePresentationAwareProductFilter = (tableFilter, _includeAttributeStatuses = true) => {
    const rawPresentationAttributes = [];
    const productFilter = (0, table_filter_shared_utils_1.buildBaseProductFilterFromTableFilter)(tableFilter, (fieldKey, filterData, operator) => {
        const presentationAttributeFilter = (0, exports.createProductPresentationAttributeStateFilter)(fieldKey, filterData, operator);
        if (presentationAttributeFilter) {
            rawPresentationAttributes.push(presentationAttributeFilter);
        }
        return null;
    });
    const presentationAttributes = tableFilter.logicOperator === 'or'
        ? rawPresentationAttributes
        : mergeExclusionOnlyPresentationAttributeFilters(rawPresentationAttributes);
    return Object.assign(Object.assign({}, hasProductFilterCriteria(productFilter) && { product: productFilter }), presentationAttributes.length && {
        presentationAttributes,
        presentationAttributeFilterStrategy: tableFilter.logicOperator === 'or' ? graphql_1.FilterStrategy.Or : graphql_1.FilterStrategy.And,
    });
};
exports.generatePresentationAwareProductFilter = generatePresentationAwareProductFilter;
